import {
    Box,
    Chair,
    Cup,
    Desk,
    GlassPanel,
    Lamp,
    Laptop,
    P,
    Pendant,
    PersonSeated,
    PersonStanding,
    Plant,
    Shadow,
    Slab,
    pts,
} from "@/components/workspaces/IsoKit";

export const SharedDefs = () => (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
            <linearGradient id="wsScreen" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#9A5FC4" />
                <stop offset="1" stopColor="#2BB3A3" />
            </linearGradient>
            <linearGradient id="wsGlass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#CDE1FA" stopOpacity="0.38" />
                <stop offset="1" stopColor="#AAC8F0" stopOpacity="0.12" />
            </linearGradient>
            <linearGradient id="wsRug" gradientUnits="userSpaceOnUse" x1="180" y1="260" x2="420" y2="420">
                <stop offset="0" stopColor="#7E35A8" stopOpacity="0.08" />
                <stop offset="1" stopColor="#22B3A3" stopOpacity="0.08" />
            </linearGradient>
            <radialGradient id="wsGlow">
                <stop offset="0" stopColor="#FFD896" stopOpacity="0.55" />
                <stop offset="1" stopColor="#FFD896" stopOpacity="0" />
            </radialGradient>
        </defs>
    </svg>
);

const SceneShell = ({ label, children }) => (
    <svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" className="h-auto w-full" role="img" aria-label={label}>
        {children}
    </svg>
);

export const CoworkingVisual = () => (
    <SceneShell label="Conceptual visualization of the Work Island coworking area">
        <polygon points={pts(P(-160, -60, 20.5), P(80, -60, 20.5), P(80, 130, 20.5), P(-160, 130, 20.5))} fill="url(#wsRug)" />
        <Slab />
        <Shadow x={-20} y={15} rx={120} ry={36} />
        <Desk x={-20} y={-25} />
        <Desk x={-20} y={55} />
        <Chair x={-75} y={-60} facing={1} />
        <Chair x={35} y={-60} facing={1} />
        <Chair x={-75} y={10} facing={-1} />
        <Chair x={35} y={90} facing={-1} />
        <PersonSeated x={-75} y={-60} color="#33415C" />
        <PersonSeated x={35} y={-60} color="#2AA79A" />
        <PersonSeated x={35} y={90} color="#526179" />
        <Laptop x={-75} y={-31} z={58} />
        <Laptop x={35} y={-31} z={58} />
        <Laptop x={35} y={49} z={58} flip />
        <Lamp x={-125} y={-21} z={58} />
        <Cup x={20} y={-29} z={58} />
        <Plant x={-160} y={95} s={0.95} />
        <Plant x={115} y={-100} s={0.75} />
    </SceneShell>
);

export const PrivateCabinVisual = () => (
    <SceneShell label="Conceptual visualization of a Work Island private cabin">
        <Slab />
        <Shadow x={-70} y={-40} rx={85} ry={26} />
        <Box x={-140} y={-15} w={22} d={54} z0={20} h={76} t="#FBFCFE" l="#E9ECF3" r="#DDE1EC" stroke="#D5DAE6" />
        {[44, 68].map((sz) => (
            <line key={sz} x1={P(-129, -38, sz)[0]} y1={P(-129, -38, sz)[1]} x2={P(-129, 8, sz)[0]} y2={P(-129, 8, sz)[1]} stroke="#D5DAE6" strokeWidth={1} />
        ))}
        <Box x={-70} y={-40} w={90} d={36} z0={50} h={6} t="#E7CDA8" l="#C9A87F" r="#B8946C" stroke="#D9BC93" />
        <Chair x={-70} y={-75} facing={1} />
        <PersonSeated x={-70} y={-73} color="#33415C" />
        <Laptop x={-85} y={-38} z={56} />
        <Lamp x={-45} y={-44} z={56} />
        <Plant x={-128} y={-100} s={0.75} />
        <Plant x={125} y={105} s={0.9} />
        <Pendant x={-70} y={-40} />
        <GlassPanel x1={-160} y1={40} x2={20} y2={40} />
        <GlassPanel x1={20} y1={-120} x2={20} y2={40} />
        {[-113, -66].map((fx) => {
            const b = P(fx, 40, 20);
            const t = P(fx, 40, 130);
            return <line key={fx} x1={b[0]} y1={b[1]} x2={t[0]} y2={t[1]} stroke="rgba(110,132,182,0.45)" strokeWidth={1.1} />;
        })}
        {[-80, -40].map((fy) => {
            const b = P(20, fy, 20);
            const t = P(20, fy, 130);
            return <line key={fy} x1={b[0]} y1={b[1]} x2={t[0]} y2={t[1]} stroke="rgba(110,132,182,0.45)" strokeWidth={1.1} />;
        })}
    </SceneShell>
);

export const PremiumCabinVisual = () => (
    <SceneShell label="Conceptual visualization of a Work Island premium cabin">
        <polygon points={pts(P(-150, -130, 20), P(150, -130, 20), P(150, -130, 110), P(-150, -130, 110))} fill="#F3F5FA" stroke="#E3E7F0" strokeWidth={1} />
        <circle cx={P(40, -129, 72)[0]} cy={P(40, -129, 72)[1]} r={10} fill="#7E35A8" opacity={0.3} />
        <circle cx={P(66, -129, 62)[0]} cy={P(66, -129, 62)[1]} r={6} fill="#22B3A3" opacity={0.3} />
        <Slab />
        <polygon points={pts(P(-140, -70, 20.5), P(140, -70, 20.5), P(140, 120, 20.5), P(-140, 120, 20.5))} fill="#ECEFF7" />
        <Shadow x={0} y={-10} rx={110} ry={32} />
        <Box x={-140} y={-60} w={60} d={26} z0={20} h={42} t="#FBFCFE" l="#E9ECF3" r="#DDE1EC" stroke="#D5DAE6" />
        <Box x={-142} y={-66} w={20} d={14} z0={62} h={12} t="#5B79B8" l="#4C67A2" r="#415A92" />
        <Box x={0} y={-30} w={110} d={44} z0={50} h={6} t="#E7CDA8" l="#C9A87F" r="#B8946C" stroke="#D9BC93" />
        <Chair x={0} y={-72} facing={1} />
        <PersonSeated x={0} y={-70} color="#16324F" />
        <Chair x={-35} y={25} facing={-1} />
        <Chair x={45} y={25} facing={-1} />
        <Laptop x={-15} y={-32} z={56} />
        <Cup x={25} y={-36} z={56} />
        <Plant x={140} y={-100} s={0.9} />
        <g>
            <line x1={P(135, 10, 20)[0]} y1={P(135, 10, 20)[1]} x2={P(135, 10, 95)[0]} y2={P(135, 10, 95)[1]} stroke="#8A94AC" strokeWidth={2.4} />
            <polygon points={pts(P(124, 10, 78), P(146, 10, 78), P(141, 10, 96), P(129, 10, 96))} fill="#B9A06B" />
            <ellipse cx={P(135, 10, 66)[0]} cy={P(135, 10, 66)[1]} rx={20} ry={8} fill="url(#wsGlow)" opacity={0.5} />
        </g>
    </SceneShell>
);

export const MeetingRoomVisual = () => (
    <SceneShell label="Conceptual visualization of a Work Island meeting room">
        <polygon points={pts(P(-60, -125, 58), P(60, -125, 58), P(60, -125, 138), P(-60, -125, 138))} fill="#16324F" />
        <polygon points={pts(P(-44, -124, 72), P(-24, -124, 72), P(-24, -124, 98), P(-44, -124, 98))} fill="#7E35A8" opacity={0.65} />
        <polygon points={pts(P(-12, -124, 72), P(8, -124, 72), P(8, -124, 112), P(-12, -124, 112))} fill="#2B5CAB" opacity={0.65} />
        <polygon points={pts(P(20, -124, 72), P(40, -124, 72), P(40, -124, 122), P(20, -124, 122))} fill="#22B3A3" opacity={0.65} />
        <Box x={0} y={-125} w={26} d={6} z0={20} h={38} t="#46536F" l="#3A4763" r="#333F59" />
        <Slab />
        <Shadow x={0} y={10} rx={105} ry={32} />
        <Chair x={-50} y={-28} facing={1} />
        <Chair x={50} y={-28} facing={1} />
        <Chair x={-50} y={48} facing={-1} />
        <Chair x={50} y={48} facing={-1} />
        <Box x={0} y={10} w={120} d={10} z0={20} h={32} t="#C4CCDD" l="#AEB7CB" r="#9AA5BE" />
        <Box x={0} y={10} w={170} d={60} z0={52} h={6} t="#FFFFFF" l="#E9ECF3" r="#DDE1EC" stroke="#D5DAE6" />
        <PersonSeated x={-50} y={-26} color="#33415C" />
        <PersonStanding x={92} y={-80} color="#3E4C66" />
        <Laptop x={-20} y={4} z={58} />
        <Cup x={30} y={16} z={58} />
        <Cup x={-60} y={22} z={58} />
        <Pendant x={-40} y={10} />
        <Pendant x={40} y={10} />
        <Plant x={-160} y={-100} s={0.8} />
    </SceneShell>
);

export const VISUALS = {
    coworking: CoworkingVisual,
    private: PrivateCabinVisual,
    premium: PremiumCabinVisual,
    meeting: MeetingRoomVisual,
};
